import { useState } from "react";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { HiExclamation } from "react-icons/hi";

import { StatusDot } from "../../../../components";
import { useIntegrations } from "../../../../contexts/IntegrationsContext";
import { useAuth, useToast } from "../../../../hooks";
import {
  connectIntegration as connectIntegrationService,
  disconnectIntegration as disconnectIntegrationService,
  getTeamName,
  AUTH_TYPES,
} from "../../../../utils";
import { DisconnectConfirmation } from "../DisconnectConfirmation";

const AMPLITUDE_HOSTNAMES = {
  US: "amplitude.com",
  EU: "analytics.eu.amplitude.com",
};

export const AmplitudeHeader = ({ isConnected, integration, credentials }) => {
  const { teamId, userId, user, apiClient, getOrCreateNangoSessionToken } =
    useAuth();
  const { fetchAllIntegrations } = useIntegrations();
  const showToast = useToast();

  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [error, setError] = useState(null);

  // Validate credentials before connecting
  const validateCredentials = () => {
    if (!credentials?.api_key?.trim()) {
      setError("API Key is required");
      return false;
    }
    if (!credentials?.secret_key?.trim()) {
      setError("Secret Key is required");
      return false;
    }
    return true;
  };

  const handleConnect = async () => {
    if (!validateCredentials()) {
      return;
    }

    setConnectLoading(true);
    setError(null);

    try {
      const teamName = await getTeamName(apiClient);
      const region = credentials.region || "US";
      const hostname = AMPLITUDE_HOSTNAMES[region];

      await connectIntegrationService({
        integrationId: "amplitude",
        authType: AUTH_TYPES.OAUTH,
        authConfig: {
          credentials: {
            username: credentials.api_key,
            password: credentials.secret_key,
          },
          // Pass hostname so Nango uses the correct region for verification and syncs
          // See: https://nango.dev/docs/api-integrations/amplitude/connect#step-3-finding-your-hostname-optional
          params: { hostname },
        },
        apiClient,
        teamId,
        userId,
        userEmail: user.email,
        showToast,
        getOrCreateNangoSessionToken,
        teamName,
        endpoint: "integration/team-integrations/",
        onSuccess: async () => {
          await fetchAllIntegrations();
        },
        onError: (err) => {
          setError(
            err.message || "Failed to connect Amplitude. Please try again.",
          );
        },
      });
    } catch (err) {
      // Error already handled in connectIntegration
    } finally {
      setConnectLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnectLoading(true);
    setError(null);

    try {
      await disconnectIntegrationService({
        integrationId: "amplitude",
        integration,
        apiClient,
        showToast,
        endpoint: "integration/team-integrations/",
        onSuccess: async () => {
          setShowDisconnectModal(false);
          await fetchAllIntegrations();
        },
        onError: (err) => {
          setError(
            err.message || "Failed to disconnect Amplitude. Please try again.",
          );
        },
      });
    } catch (err) {
      // Error already handled in disconnectIntegration
    } finally {
      setDisconnectLoading(false);
    }
  };

  const isButtonLoading = connectLoading || disconnectLoading;

  return (
    <>
      {error && (
        <div className="mt-4 px-6">
          <div className="rounded-md bg-red-50 p-3">
            <div className="flex">
              <HiExclamation className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-row items-center justify-between px-6">
        <div className="flex flex-row items-center justify-center gap-2.5">
          <span className="mr-2 text-xl font-bold text-blue-600">
            Amplitude
          </span>
          {isButtonLoading ? (
            <AiOutlineLoading className="h-6 w-6 animate-spin" />
          ) : (
            <StatusDot status={isConnected ? "green" : undefined} />
          )}
        </div>

        <div className="flex flex-row items-center justify-between gap-2">
          <Button
            color={isConnected ? "gray" : "blue"}
            className="w-36"
            disabled={isButtonLoading}
            isProcessing={isButtonLoading}
            onClick={
              isConnected ? () => setShowDisconnectModal(true) : handleConnect
            }
          >
            {isConnected ? "Disconnect" : "Connect"}
          </Button>
        </div>
      </div>

      <DisconnectConfirmation
        show={showDisconnectModal}
        onConfirm={handleDisconnect}
        onCancel={() => setShowDisconnectModal(false)}
        integrationName="Amplitude"
      />
    </>
  );
};
